import { ProductReviewsData } from '@/components/CustomerReviews/CustomerReviews';
import { ImplAPI, Req } from './ImplAPI';

// ============================================================================
// Types & Interfaces
// ============================================================================

// Standard Judge.me Review object based on their API specifications
export interface JudgeMeReview {
  id: number;
  title: string;
  body: string;
  rating: number;
  product_external_id: string;
  reviewer: {
    id: number;
    email: string;
    name: string;
    phone?: string;
  };
  source: string;
  curated: string;
  published: boolean;
  hidden: boolean;
  verified: string;
  created_at: string;
  updated_at: string;
  pictures?: Array<{
    hidden: boolean;
    urls: {
      small: string;
      compact: string;
      huge: string;
      original: string;
    };
  }>;
}

export interface GetReviewsResponse {
  current_page: number;
  per_page: number;
  reviews: JudgeMeReview[];
}

export interface GetReviewsParams {
  productId?: string; // If targeting a specific product
  page?: number;
  perPage?: number;
  rating?: number; // Filter by star rating
}

// Payload for POSTing a review through your internal API
export interface CreateReviewBody {
  handle: string;
  rating: number;
  title: string;
  body: string;
  name: string;
  email: string;
  shop_domain: string;
  platform: string;
}

export interface CreateReviewResponse {
  success: boolean;
  message?: string;
}

// ============================================================================
// API Class Implementation
// ============================================================================

export class ReviewAPI extends ImplAPI {
  // This route is used for your internal backend POST request
  static get route() {
    return '/api/reviews';
  }

  /**
   * GET REVIEWS (EXTERNAL)
   * Fetches reviews directly from the Judge.me API.
   * Requires VITE_JUDGEME_PUBLIC_TOKEN and VITE_SHOPIFY_STORE_DOMAIN in your .env
   */

  static async get({ handle }: { handle: string }) {
    // Point to your own backend route
    // This avoids CORS because your backend isn't restricted by browser origin policies
    return Req<ProductReviewsData>({
      method: 'GET',
      route: `/api/reviews/product?handle=${handle}`,
    });
  }

  /**
   * POST REVIEW (INTERNAL)
   * Posts a new review through your backend proxy.
   */
  static async post(body: CreateReviewBody) {
    return Req<CreateReviewResponse, CreateReviewBody>({
      method: 'POST',
      route: '', // Evaluates to '/api/reviews'
      body: body,
      uri: 'https://judge.me/api/v1/reviews',
      credentials: 'omit', // Adjust to 'include' if your backend requires cookies/sessions
    });
  }
}
